/**
 * Paths to JSON Converter for Tasks
 *
 * Converts array of task paths to full JSON objects with data
 * Used for preparing data for AI server submission
 */

import type { Task } from '@segmently/schema-models/entities';
import { parsePath } from '../../shared/pathParser';

/**
 * Converts task paths to JSON objects with full data
 *
 * @param paths - Array of task paths
 * @param tasks - Loaded task entities
 * @returns Array of partial task objects with selected data
 */
export function pathsToJson(paths: string[], tasks: Task[]): Partial<Task>[] {
  // Group paths by task ID
  const taskMap = new Map<string, Set<string>>();

  for (const path of paths) {
    const parsed = parsePath(path);
    if (!parsed || parsed.entityType !== 'task' || !parsed.entityId) continue;

    if (!taskMap.has(parsed.entityId)) {
      taskMap.set(parsed.entityId, new Set());
    }
    taskMap.get(parsed.entityId)!.add(path);
  }

  const result: Partial<Task>[] = [];

  // Process each task
  for (const [taskId, taskPaths] of taskMap) {
    const fullTask = tasks.find((t) => t.id === taskId);
    if (!fullTask) continue;

    // Check if whole task is selected
    const hasWholeTask = Array.from(taskPaths).some((p) => {
      const parsed = parsePath(p);
      return parsed?.type === 'entity';
    });

    if (hasWholeTask) {
      // Whole task selected - include all data
      result.push(fullTask);
      continue;
    }

    // Partial selection - collect only selected fields
    const partialTask: Partial<Task> = {
      id: taskId,
    };

    const selectedFields = new Set<string>();
    const selectedArrays = new Map<string, Set<number>>();

    for (const path of taskPaths) {
      const parsed = parsePath(path);
      if (!parsed) continue;

      if (parsed.type === 'field' && parsed.fieldName) {
        // Single field selected
        selectedFields.add(parsed.fieldName);
      } else if (parsed.type === 'subcollection' && parsed.subcollectionName) {
        // Whole array selected (e.g., funnelGoals)
        selectedFields.add(parsed.subcollectionName);
      } else if (parsed.type === 'arrayItem' && parsed.fieldName && parsed.arrayIndex !== undefined) {
        // Individual array item selected
        if (!selectedArrays.has(parsed.fieldName)) {
          selectedArrays.set(parsed.fieldName, new Set());
        }
        selectedArrays.get(parsed.fieldName)!.add(parsed.arrayIndex);
      }
    }

    // Add selected fields
    for (const fieldName of selectedFields) {
      if (fieldName in fullTask) {
        (partialTask as any)[fieldName] = (fullTask as any)[fieldName];
      }
    }

    // Add selected array items
    for (const [fieldName, indices] of selectedArrays) {
      if (!(fieldName in fullTask)) continue;

      const fullArray = (fullTask as any)[fieldName];
      if (!Array.isArray(fullArray)) continue;

      // If whole array already included via selectedFields, skip
      if (selectedFields.has(fieldName)) continue;

      // Collect selected items
      const selectedItems: any[] = [];
      for (const index of indices) {
        if (index < fullArray.length) {
          selectedItems.push(fullArray[index]);
        }
      }

      if (selectedItems.length > 0) {
        (partialTask as any)[fieldName] = selectedItems;
      }
    }

    result.push(partialTask);
  }

  return result;
}
